'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { type ComponentProps, useRef } from 'react';

type IntentPrefetchLinkProps = Omit<ComponentProps<typeof Link>, 'prefetch'>;

/**
 * Internal navigation link that prefetches on intent (hover, focus, touch)
 * instead of on viewport entry.
 *
 * `router.prefetch(href)` with no kind fetches the same per-URL static shell
 * the default `<Link>` prefetch does (with `partialPrefetching` OFF in
 * next.config.ts) — the whole prerendered page from the CDN — so a hovered or
 * touched link still navigates with its content and no loading state. Never
 * pass `prefetch={true}` or a FULL kind: in this mode that means a dynamic,
 * uncached server render per link.
 *
 * Why not viewport prefetch (2026-09-28): every in-viewport link cost a proxy
 * invocation + CDN request, and headless renderers (Googlebot, Merchant
 * Center) see the whole page as in-viewport, so each crawl prefetched every
 * footer, menu, and product-card link. Production logs showed ~96% of
 * storefront requests were these prefetch bursts; stale targets also
 * triggered background ISR re-renders nobody navigated to.
 *
 * History: under Partial Prefetching (2026-08-23, reverted the same day) an
 * earlier intent policy upgraded to a `private, no-store` runtime prefetch on
 * hover; this one only ever fetches the static shell.
 */
export function IntentPrefetchLink({
	onMouseEnter,
	onTouchStart,
	onFocus,
	...props
}: IntentPrefetchLinkProps) {
	const router = useRouter();
	const prefetched = useRef(false);
	const intent = () => {
		const { href } = props;
		if (prefetched.current || typeof href !== 'string' || !href.startsWith('/')) return;
		prefetched.current = true;
		router.prefetch(href);
	};
	return (
		<Link
			{...props}
			prefetch={false}
			onMouseEnter={(event) => {
				onMouseEnter?.(event);
				intent();
			}}
			onTouchStart={(event) => {
				onTouchStart?.(event);
				intent();
			}}
			onFocus={(event) => {
				onFocus?.(event);
				intent();
			}}
		/>
	);
}
